import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import about1 from '../../assets/images/about1.jpg';
import about2 from '../../assets/images/about2.jpg';
import about3 from '../../assets/images/about3.jpg';
import about4 from '../../assets/images/about4.jpg';
import './CulinaryJourney.css';

interface JourneyStep {
    id: string;
    titleKey: string;
    descriptionKey: string;
    image: string;
}

const journeySteps: JourneyStep[] = [
    {
        id: 'menu-development',
        titleKey: 'culinaryJourney.steps.menuDevelopment.title',
        descriptionKey: 'culinaryJourney.steps.menuDevelopment.description',
        image: about1
    },
    {
        id: 'supply-chain',
        titleKey: 'culinaryJourney.steps.supplyChain.title',
        descriptionKey: 'culinaryJourney.steps.supplyChain.description',
        image: about2
    },
    {
        id: 'food-preparation',
        titleKey: 'culinaryJourney.steps.foodPreparation.title',
        descriptionKey: 'culinaryJourney.steps.foodPreparation.description',
        image: about3
    },
    {
        id: 'food-production',
        titleKey: 'culinaryJourney.steps.foodProduction.title',
        descriptionKey: 'culinaryJourney.steps.foodProduction.description',
        image: about4
    },
    {
        id: 'portioning',
        titleKey: 'culinaryJourney.steps.portioning.title',
        descriptionKey: 'culinaryJourney.steps.portioning.description',
        image: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=1200&q=80&auto=format&fit=crop'
    },
    {
        id: 'delivery',
        titleKey: 'culinaryJourney.steps.delivery.title',
        descriptionKey: 'culinaryJourney.steps.delivery.description',
        image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80&auto=format&fit=crop'
    }
];

const CulinaryJourney: React.FC = () => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);

    const handlePrev = () => {
        setActiveStep((prev) => (prev === 0 ? journeySteps.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setActiveStep((prev) => (prev === journeySteps.length - 1 ? 0 : prev + 1));
    };

    const currentStep = journeySteps[activeStep];

    return (
        <section className="culinary-journey-section">
            <div className="culinary-journey-container">
                {/* Left side - Title and description */}
                <motion.div
                    className="culinary-journey-header"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="culinary-journey-title">
                        <span className="title-line">{t('culinaryJourney.title.line1')}</span>
                        <span className="title-line accent">{t('culinaryJourney.title.line2')}</span>
                    </h2>
                    <div className="culinary-journey-description">
                        {t('culinaryJourney.description').split('\n\n').map((paragraph: string, index: number) => (
                            <p key={index} className="description-paragraph">
                                {paragraph}
                            </p>
                        ))}
                    </div>
                </motion.div>

                {/* Right side - Carousel */}
                <motion.div
                    className="culinary-journey-carousel"
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="carousel-wrapper">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep.id}
                                className="carousel-slide"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="slide-image-container">
                                    <img
                                        src={currentStep.image}
                                        alt={t(currentStep.titleKey)}
                                        className="slide-image"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <div className="slide-overlay"></div>
                                </div>
                                <div className="slide-content">
                                    <h4 className="slide-title">{t(currentStep.titleKey)}</h4>
                                    <p className="slide-description">{t(currentStep.descriptionKey)}</p>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation */}
                        <div className="carousel-navigation">
                            <button
                                className="nav-button prev"
                                onClick={handlePrev}
                                aria-label="Previous step"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>
                            <div className="carousel-indicators">
                                {journeySteps.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`indicator ${index === activeStep ? 'active' : ''}`}
                                        onClick={() => setActiveStep(index)}
                                        aria-label={`Go to step ${index + 1}`}
                                    />
                                ))}
                            </div>
                            <button
                                className="nav-button next"
                                onClick={handleNext}
                                aria-label="Next step"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default CulinaryJourney;
